import { memo } from 'react';
import { usePitchField } from './context.js';
import { useSelectionStore } from '@/hooks/useSelection.js';
import { useKeyboardFocusStore } from '@/hooks/useKeyboardFocus.js';
import { useOccupiedStore } from '@/hooks/useOccupied.js';

const EMPTY_OBJ = {};

/**
 * Single SVG tile — a <rect> inside the grid.
 *
 * Performance:
 *  - React.memo prevents re-renders when props don't change.
 *  - useSelectionStore selector returns a stable boolean (O(1) object lookup)
 *    so this tile only re-renders when ITS own selection state changes.
 *  - useKeyboardFocusStore selector checks only this tile's ID — only the two
 *    tiles involved in a focus transition re-render; Grid is unaffected.
 *  - No hover state — CSS :hover handles the visual entirely.
 *    Hover tooltips are rendered by the single HoverTooltip component in Grid.
 *
 * Accessibility:
 *  - role/aria-checked/aria-label are NOT set on the <rect> because the SVG
 *    is aria-hidden="true". Screen reader access is handled by the <div tabIndex>
 *    wrapper + FocusedAnnouncement live region in Grid.jsx.
 *
 * Keyboard interaction is handled by the parent Grid component.
 * Pointer events (vs mouse-only events) ensure drag-select works on touch.
 */
const Tile = memo( function Tile( { tile, onTilePointerDown, onTileDragEnter } ) {
	const { mode, fieldType } = usePitchField();

	// O(1) object-set lookup — stable boolean, no array scan
	const isSelected = useSelectionStore(
		( s ) => !! ( s.selected[ fieldType ]?.[ tile.id ] )
	);

	// Only this tile re-renders when focus moves to/from it
	const isFocused = useKeyboardFocusStore(
		( s ) => s.hasFocus && s.fieldType === fieldType && s.focusedId === tile.id
	);

	// Only this tile re-renders when ITS occupied status changes (not on every poll)
	const occEntry   = useOccupiedStore( ( s ) => ( s.expandedCache[ fieldType ] ?? EMPTY_OBJ )[ tile.id ] ?? null );
	const isOccupied = !! occEntry;

	const state =
		isOccupied   ? ( occEntry.status === 'publish' ? 'occupied' : 'pending' )
		: isSelected ? 'selected'
		:              'free';

	function handlePointerDown( e ) {
		// Ignore non-primary mouse buttons; allow all touch/stylus
		if ( e.pointerType === 'mouse' && e.button !== 0 ) return;
		// Prevent scroll on touch/stylus during drag-select
		if ( e.pointerType !== 'mouse' ) e.preventDefault();
		// Release implicit pointer capture so pointerenter fires on other tiles during drag
		e.target.releasePointerCapture( e.pointerId );
		onTilePointerDown?.( tile.id, [ tile.id ], isOccupied );
	}

	return (
		<g
			data-nav-id={tile.id}
			className={`wsp-tile wsp-tile--${ state }${ isFocused ? ' wsp-tile--keyboard-focus' : '' }`}
			style={tile.customStyle ?? undefined}
			onPointerEnter={() => onTileDragEnter?.( tile.id, [ tile.id ], isOccupied )}
		>
			<rect
				x={tile.x}
				y={tile.y}
				width={tile.w}
				height={tile.h}
				className="wsp-tile__rect"
				onPointerDown={handlePointerDown}
			/>

			{isFocused && (
				<text
					x={tile.x + tile.w / 2}
					y={tile.y + tile.h / 2}
					className="wsp-keyboard-cursor"
					textAnchor="middle"
					dominantBaseline="central"
					fontSize={Math.min( tile.w, tile.h ) * 0.7}
					aria-hidden="true"
				>
					◎
				</text>
			)}
		</g>
	);
} );

export default Tile;
